/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  FC,
  memo,
  useEffect,
  useState,
  lazy,
  Suspense,
  useRef,
  useCallback,
  useMemo,
} from 'react'

import { useFocusNodeById, useSetFocus } from '@please/lrud'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import {
  EndActions,
  EventType,
} from '@sportworld/js-sdk/src/types/servicesTypes/configTypes'
import { getContentAccessFlags } from '@sportworld/js-sdk/src/utils'

import { ChannelVideoDebugInfo } from '@/components/ChannelVideoDebugInfo/ChannelVideoDebugInfo'
import { BroadcastHtmlWidget } from '@/components/HtmlWidget/HtmlWidget'
import { TileImage } from '@/components/Tiles/TileImage'
import {
  useVideoOverlay,
  VideoOverlayTypes,
} from '@/components/VideoPlayer/hooks/useVideoOverlay'
import { eventAction, eventObject } from '@/constants/analytics'
import { VideoPlaybackStatus } from '@/constants/common'
import { focusIds } from '@/constants/focus'
import { sliderItemImgPlaceholder } from '@/constants/icons'
import { popupStatus } from '@/constants/popup'
import { channelSliderImgWidth } from '@/constants/tiles'
import { useActiveBroadcast } from '@/hooks/useActiveBroadcast'
import { useSwitchChannels } from '@/hooks/useSwitchChannels'
import useStore from '@/store/useStore'
import { VideoViewedType } from '@/types/analyticsTypes'
import { VideoPlayerProps } from '@/types/componentsPropsTypes'
import {
  ChannelType,
  eventTypes,
  ResolvedAdSettings,
} from '@/types/servicesTypes/configTypes'
import { sendAnalyticsEvent } from '@/utils/analyticsUtils'
import { findNearestByImgWidth, getVideoForChannel } from '@/utils/utils'
import { replaceVideoUrlParams } from '@/utils/videoParamsUtils'

import {
  BroadcastMainElem,
  BroadcastMainContainer,
  PlaceholderWrapper,
} from './styles'

const VideoPlayer: FC<VideoPlayerProps> = lazy(
  () => import('@/components/VideoPlayer/VideoPlayer'),
)

type Props = {
  isClassicHome?: boolean
  isStageHome?: boolean
  channel?: ChannelType
  isPopup: boolean
  broadcastId: string
  isMuted?: boolean
  focusIdPrefix?: string
  scrollToSection?: () => void
  setVideoPlaybackStatus?: (value: Nullable<VideoPlaybackStatus>) => void
  search?: string
  isInactiveSectionGrid?: boolean
}

const BroadcastMainRaw: FC<Props> = ({
  isClassicHome,
  isStageHome,
  channel,
  isPopup,
  broadcastId,
  isMuted,
  focusIdPrefix = '',
  scrollToSection,
  setVideoPlaybackStatus,
  search,
  isInactiveSectionGrid,
}) => {
  const setFocus = useSetFocus()
  const setListPopup = useStore((state) => state.common.setListPopup)
  const { isRegisteredUser } = useAuth()

  const [src, setSrc] = useState<string>('')
  const [videoData, setVideoData] = useState<VideoViewedType>()
  const [withUserInteraction, setWithUserInteraction] = useState(false)
  const [isActiveChannelChanged, setIsActiveChannelChanged] = useState(false)
  const [adSettings, setAdSettings] =
    useState<Nullable<ResolvedAdSettings>>(null)

  const {
    showVideoControls,
    videoControlsVisible,
    isFullscreen,
    setIsFullscreen,
  } = useStore((state) => state.player)
  const event = useStore((state) => state.common.currentEvent)
  const listPopup = useStore((state) => state.common.listPopup)
  const mediaVideo = useStore((state) => state.common.mediaVideo)
  const youTubeVideo = useStore((state) => state.common.youTubeVideo)
  const isEnabledImaSdkDebug =
    useStore.getState().common.appSettings.isEnabledImaSdkDebug
  const setActiveBroadcastChannel = useStore(
    (state) => state.common.setActiveBroadcastChannel,
  )
  const { pagePopupStatus } = useStore((state) => state.popup)
  const { activeChannel } = useActiveBroadcast(broadcastId)
  const channelEvent = useStore(
    (state) => state.common.channelEvents[activeChannel?.id || ''],
  )
  const currentEvent =
    (!channelEvent?.isFutureChannelEvent && channelEvent) || null

  const { isAccessible, isRequiresRegistrationOnly } = useMemo(() => {
    return getContentAccessFlags(currentEvent, activeChannel, isRegisteredUser)
  }, [currentEvent, activeChannel, isRegisteredUser])

  const isChannelWatchable = !!activeChannel?.watchable

  const isBlocked = isAccessible === false
  const isBroadcastHtmlWidget =
    activeChannel?.htmlWidgetUrl && !isBlocked && isChannelWatchable

  const isEventPopupOpen = Boolean(event)
  const isPagePopupOpen = pagePopupStatus !== popupStatus.CLOSED
  const focusId =
    focusIdPrefix +
    `${isStageHome ? 'stageHome' : broadcastId}` +
    'BroadcastMainElem'
  const isVideoMuted = isEventPopupOpen || !!listPopup || isMuted
  const isVideoDisplayed =
    !mediaVideo &&
    !youTubeVideo &&
    (isPopup || !isPagePopupOpen) &&
    !isInactiveSectionGrid
  const isStageInBackground = isStageHome && !isFullscreen
  const channelSliderFocusId = `${focusIds.CHANNEL_SLIDER}${focusIds.LIVE_TODAY}`
  const isChannelSliderFocused =
    useFocusNodeById(channelSliderFocusId)?.isFocused

  const { videoOverlay, setVideoOverlay } = useVideoOverlay({
    activeChannel,
  })

  const hasVideoBlockedOverlay =
    videoOverlay?.overlayType == VideoOverlayTypes.BLOCKED

  useSwitchChannels({
    broadcastId,
    isActive: isStageHome
      ? isFullscreen && !isChannelSliderFocused
      : isVideoDisplayed && !isChannelSliderFocused,
  })

  const timeoutIdRef = useRef<NodeJS.Timeout>()
  useEffect(() => {
    clearTimeout(timeoutIdRef.current)
    return () => {
      timeoutIdRef.current = setTimeout(() => {
        setIsFullscreen(false)
      })
    }
  }, [])

  useEffect(() => {
    if (isFullscreen) {
      setFocus(focusId)
    }
  }, [isFullscreen])

  useEffect(() => {
    if (
      isStageHome &&
      (hasVideoBlockedOverlay || !isChannelWatchable) &&
      isFullscreen
    ) {
      setIsFullscreen(false)
    }
  }, [isFullscreen, isStageHome, hasVideoBlockedOverlay, isChannelWatchable])

  useEffect(() => {
    if (channel) {
      setActiveBroadcastChannel(broadcastId, channel)
    }
  }, [broadcastId, channel])

  const updateAdSettings = useCallback(() => {
    /**
     * Event adSettings has a priority over a Channel adSettings
     */
    let adSettingsRes: Nullable<ResolvedAdSettings> = null

    if (
      currentEvent?.adSettings &&
      Object.keys(currentEvent.adSettings).length > 0
    ) {
      adSettingsRes = currentEvent.adSettings
      isEnabledImaSdkDebug &&
        console.log(
          '[BroadcastMain] override activeChannel adSettings from currentEvent',
          adSettingsRes,
        )
    } else if (
      activeChannel?.adSettings &&
      Object.keys(activeChannel.adSettings).length > 0
    ) {
      adSettingsRes = activeChannel.adSettings
      isEnabledImaSdkDebug &&
        console.log(
          '[BroadcastMain] adSettings from activeChannel',
          adSettingsRes,
        )
    }
    setAdSettings(adSettingsRes)

    isDevMode &&
      console.log('[BroadcastMain] set resulting setAdSettings', {
        adSettings: adSettingsRes,
      })
  }, [currentEvent, activeChannel, isEnabledImaSdkDebug])

  const videoDataUpdate = (
    videoUrl: string,
    activeChannel: ChannelType,
    format: string,
    seekToLiveEdge: boolean,
    onEnded?: EndActions,
    currentEvent?: EventType | null,
  ) => {
    setVideoData({
      type: eventTypes.CHANNEL,
      url: videoUrl || '',
      format,
      media_name: activeChannel?.name,
      id: activeChannel?.id,
      onEnded,
      seekToLiveEdge,
      partnerIds: currentEvent?.partnerIds || activeChannel.partnerIds,
    })
    setSrc(videoUrl || '')
  }

  useEffect(() => {
    if (!activeChannel) {
      return
    }
    isActiveChannelChanged && setWithUserInteraction(true)
    setIsActiveChannelChanged(true)
  }, [activeChannel])

  const getMediaData = useCallback(
    async (activeChannel: ChannelType) => {
      const {
        src: videoSrc,
        format,
        onEnded,
        seekToLiveEdge,
      } = await getVideoForChannel(
        activeChannel,
        isAccessible,
        isChannelWatchable,
        hasVideoBlockedOverlay,
        currentEvent,
      )

      const videoUrl = replaceVideoUrlParams({
        url: videoSrc,
        isMuted,
        isAutoplay: !withUserInteraction,
        isFullscreen: isFullscreen,
        isVod: false,
      })

      videoDataUpdate(
        videoUrl,
        activeChannel,
        format,
        seekToLiveEdge,
        onEnded,
        currentEvent,
      )

      updateAdSettings()
    },
    [
      activeChannel,
      isAccessible,
      isChannelWatchable,
      hasVideoBlockedOverlay,
      withUserInteraction,
      isFullscreen,
      currentEvent,
    ],
  )

  useEffect(() => {
    if (!broadcastId || !activeChannel || isBroadcastHtmlWidget) {
      return
    }
    getMediaData(activeChannel)
  }, [
    activeChannel,
    isAccessible,
    broadcastId,
    isChannelWatchable,
    hasVideoBlockedOverlay,
  ])

  useEffect(() => {
    const isVideoHidden = !isVideoDisplayed || isVideoMuted
    isVideoHidden && setWithUserInteraction(false)
  }, [isVideoDisplayed, isVideoMuted])

  const openPlayer = () => {
    if (isFullscreen || isBlocked) {
      return
    }
    setIsFullscreen(true)
    sendAnalyticsEvent({
      object: eventObject.VIDEO,
      action: eventAction.FULLSCREEN_CLICK,
      video: {
        id: videoData?.id,
        link: src,
      },
    })
  }

  const onKey = (e: LRUDEvent) => {
    if (isFullscreen) {
      e.stopPropagation()
      if (!videoControlsVisible) {
        e.preventDefault()
        showVideoControls()
      }
    }
  }

  const onExitButton = () => {
    setIsFullscreen(false)

    if (mediaVideo && mediaVideo.listOptions) {
      const listContextId = mediaVideo.listOptions.listContextId
      setListPopup(mediaVideo.listOptions)
      setListPopup(null)
      if (listContextId) {
        setFocus(listContextId)
      } else {
        setFocus(focusIds.CONTENT)
      }
    }

    sendAnalyticsEvent({
      object: eventObject.VIDEO,
      action: eventAction.BACKGROUND_CLICK,
      video: {
        id: videoData?.id,
        link: src,
      },
    })
  }

  const imgUrl = useMemo(
    () =>
      findNearestByImgWidth(
        activeChannel?.image?.sources,
        channelSliderImgWidth,
        { prioritizeSvg: true },
      )?.url,
    [activeChannel?.id],
  )

  return (
    <BroadcastMainContainer
      focusId={focusId}
      elementType="section"
      onSelected={openPlayer}
      $isClassicHome={isClassicHome}
      $isStageHome={isStageHome}
      isTrap={isStageHome && isFullscreen}
      disabled={isStageInBackground}
      onKey={onKey}
    >
      {isVideoDisplayed ? (
        <BroadcastMainElem $isFullscreen={isFullscreen}>
          {isDevMode && !isStageHome && isVideoDisplayed && (
            <ChannelVideoDebugInfo
              eventChannel={activeChannel}
              event={channelEvent}
            />
          )}
          {isBroadcastHtmlWidget ? (
            <BroadcastHtmlWidget
              src={activeChannel.htmlWidgetUrl}
              withControls={isFullscreen}
              focusIdPrefix={focusIdPrefix}
              onExitButton={onExitButton}
              broadcastId={broadcastId}
            />
          ) : (
            <Suspense>
              <VideoPlayer
                src={src}
                withControls={isFullscreen}
                isBlocked={isBlocked}
                isMuted={isVideoMuted}
                videoData={videoData}
                onExitButton={onExitButton}
                broadcastId={broadcastId}
                currentEvent={currentEvent}
                focusIdPrefix={focusIdPrefix}
                withUserInteraction={withUserInteraction}
                scrollToSection={scrollToSection}
                onEndedAction={videoData?.onEnded}
                isStageHome={isStageHome}
                setVideoPlaybackStatus={setVideoPlaybackStatus}
                adSettings={adSettings}
                inPagePlayer
                videoOverlay={videoOverlay}
                setVideoOverlay={setVideoOverlay}
                search={search}
                isRequiresRegistrationOnly={isRequiresRegistrationOnly}
              />
            </Suspense>
          )}
        </BroadcastMainElem>
      ) : (
        <PlaceholderWrapper>
          <TileImage url={imgUrl} errorPlaceholder={sliderItemImgPlaceholder} />
        </PlaceholderWrapper>
      )}
    </BroadcastMainContainer>
  )
}

export const BroadcastMain = memo(BroadcastMainRaw)
